import React from 'react';

import { observer } from 'mobx-react';

import { SelectChangeEvent, MenuItem, Skeleton, Tooltip } from '@mui/material';

import { MonitoringMethod } from 'core/constants';

import useUpdateUser from 'stores/Manage/hooks/updateUser';

import { useStore } from 'utils/hooks/store';
import { ADMIN_AND_MANAGERS } from 'utils/roles/roleGroups';
import { MastermindRegistrationState, UserListItemResponse } from 'utils/api/modules/user/user.types';

import { ArcSelectorCellProps } from './types';
import { ArcsState, LOADING_ARCS, OrganisationArcsContext } from './organisationArcs';
import {
  StyledArcSelect,
  StyledCell,
  StyledReadOnlyLabel,
  StyledRegistrationStatus,
  StyledRetryButton,
  StyledSelectHint,
  StyledAriaDescription,
} from './styles';

const DEFAULT_VALUE = '__default__';
const SELF_MONITORED_HINT = 'ARC choice only applies to ARC-monitored workers';
const LOAD_FAILED_HINT = "Couldn't load ARCs. Click to retry";

const REGISTRATION_STATE_LABELS: Record<MastermindRegistrationState, string> = {
  unregistered: 'Unregistered',
  registering: 'Registering',
  registered: 'Registered',
  deactivated: 'Deactivated',
};

const ArcSelectorCell: React.FunctionComponent<ArcSelectorCellProps> = ({ rowData, actions }) => {
  const { session } = useStore();
  const { loadOrganisationArcs, retry: retryOwnArcs, ...ownArcs } = React.useContext(OrganisationArcsContext);
  const [pending, setPending] = React.useState<{ arc: UserListItemResponse['arc']; row: UserListItemResponse }>();
  const [otherOrganisationArcs, setOtherOrganisationArcs] = React.useState<ArcsState>(LOADING_ARCS);
  const [otherOrganisationAttempt, setOtherOrganisationAttempt] = React.useState(0);
  const updateUser = useUpdateUser();
  const hintId = React.useId();

  const canEdit = session.user ? ADMIN_AND_MANAGERS.includes(session.user.role) : false;
  const ownOrganisationUuid = session.user?.organisation?.uuid;
  // An admin sees workers of other organisations, who can only be given their own organisation's ARCs.
  const otherOrganisationUuid =
    loadOrganisationArcs && ownOrganisationUuid && rowData.organisationUuid !== ownOrganisationUuid
      ? rowData.organisationUuid
      : undefined;

  React.useEffect(() => {
    if (!otherOrganisationUuid || !loadOrganisationArcs) return undefined;
    let cancelled = false;
    setOtherOrganisationArcs(LOADING_ARCS);
    loadOrganisationArcs(otherOrganisationUuid)
      .then((loaded) => {
        if (!cancelled) setOtherOrganisationArcs({ arcs: loaded, loading: false, error: false });
      })
      .catch(() => {
        if (!cancelled) setOtherOrganisationArcs({ arcs: [], loading: false, error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [otherOrganisationUuid, loadOrganisationArcs, otherOrganisationAttempt]);

  const { arcs, loading, error } = otherOrganisationUuid ? otherOrganisationArcs : ownArcs;
  const retry = otherOrganisationUuid ? () => setOtherOrganisationAttempt((current) => current + 1) : retryOwnArcs;

  if (loading) return <Skeleton width={120} />;
  if (error) {
    return (
      <Tooltip title={LOAD_FAILED_HINT}>
        <StyledRetryButton
          type="button"
          data-testid="manage-arc-retry"
          aria-label={LOAD_FAILED_HINT}
          onClick={(e) => {
            e.stopPropagation();
            retry?.();
          }}
        >
          —
        </StyledRetryButton>
      </Tooltip>
    );
  }

  const orgDefault = arcs.find((arc) => arc.isOrganisationDefault);
  const defaultLabel = `Default${orgDefault ? ` (${orgDefault.name})` : ''}`;

  // Grid's refresh doesn't resolve when the rows land, so a saved choice holds until the refetch hands over a new row.
  const saving = pending?.row === rowData;
  const effectiveArc = saving ? pending.arc : rowData.arc;
  const currentValue = effectiveArc ? effectiveArc.uuid : DEFAULT_VALUE;
  const displayLabel = effectiveArc ? effectiveArc.name : defaultLabel;

  const currentArc = effectiveArc ? arcs.find((arc) => arc.uuid === effectiveArc.uuid) : orgDefault;
  const registrationState = currentArc?.integration === 'mastermind' ? rowData.mastermindRegistrationState : null;
  const registrationStatus = registrationState && (
    <StyledRegistrationStatus data-testid="manage-arc-registration-status">
      {REGISTRATION_STATE_LABELS[registrationState]}
    </StyledRegistrationStatus>
  );

  const handleChange = (event: SelectChangeEvent<unknown>): void => {
    const value = event.target.value as string;
    const arcUuid = value === DEFAULT_VALUE ? null : value;
    const selectedArc = arcUuid ? arcs.find((a) => a.uuid === arcUuid) : null;

    setPending({ arc: selectedArc ? { uuid: selectedArc.uuid, name: selectedArc.name } : null, row: rowData });

    let succeeded = false;
    updateUser(
      rowData.uuid,
      { arc: arcUuid },
      () => {
        if (!succeeded) setPending(undefined);
      },
      async () => {
        succeeded = true;
        actions.refreshData();
      }
    );
  };

  if (!canEdit) {
    return (
      <StyledCell>
        <StyledReadOnlyLabel data-testid="manage-arc-label">{displayLabel}</StyledReadOnlyLabel>
        {registrationStatus}
      </StyledCell>
    );
  }

  // A self-monitored worker keeps their assignment, so it applies again if they switch back;
  // the select still shows it, just locked.
  const isArcMonitored = rowData.alarmResponseProfile?.escalationType === MonitoringMethod.ArcMonitored;

  const select = (
    <StyledArcSelect
      data-testid="manage-arc-selector"
      value={currentValue}
      onChange={handleChange}
      size="small"
      $withoutBorder
      disabled={!isArcMonitored || saving}
      onClick={(e) => e.stopPropagation()}
      SelectDisplayProps={{ 'aria-label': 'ARC', ...(isArcMonitored ? {} : { 'aria-describedby': hintId }) }}
    >
      <MenuItem value={DEFAULT_VALUE}>{defaultLabel}</MenuItem>
      {/* The "Default" option stands in for the default ARC, unless the worker is explicitly assigned to it. */}
      {arcs
        .filter((arc) => !arc.isOrganisationDefault || arc.uuid === currentValue)
        .map((arc) => (
          <MenuItem key={arc.uuid} value={arc.uuid}>
            {arc.name}
          </MenuItem>
        ))}
    </StyledArcSelect>
  );

  return (
    <StyledCell>
      {isArcMonitored ? (
        select
      ) : (
        // The hint hangs off a focusable wrapper, since a disabled select takes neither focus nor clicks.
        <Tooltip title={SELF_MONITORED_HINT} describeChild>
          <StyledSelectHint data-testid="manage-arc-self-monitored-hint" tabIndex={0}>
            {select}
            <StyledAriaDescription id={hintId}>{SELF_MONITORED_HINT}</StyledAriaDescription>
          </StyledSelectHint>
        </Tooltip>
      )}
      {registrationStatus}
    </StyledCell>
  );
};

export default observer(ArcSelectorCell);
